<template>
  <div class="text-input-handler">
    <!-- Component acts as a controller for text inputs -->
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, defineProps, defineEmits, ref, watch } from 'vue';


const props = defineProps({
  buttonImagePath: {
    type: String,
    default: window.smartpress_vars.pluginUrl + '/assets/smart-press-logo-150.png'
  },
  selection: {
    type: String,
    default: ''
  },
  customFields: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['show-popup', 'close-popup', 'update-selection']);

// Store references to elements for cleanup
const managedElements = ref(new Set());
// Track the currently active input element
const activeElement = ref(null);

// Query selector for text inputs and textareas
const inputSelector = "input[type='text']:not([readonly]):not(#smart-edit-content):not(.smartpress-disabled):not(.smartpress-disabled *):not(.pcr-result) , textarea:not([readonly]):not(#smart-edit-content):not(.wp-editor-area):not(.smartpress-disabled):not(.smartpress-disabled *)";

// Add new ref for MutationObserver
const observer = ref(null);

// Add new ref for TinyMCE related items
const tinyMCECheckInterval = ref(null);
const tinyMCEEditors = ref(new Set());

const localSelection = ref(props.selection);

watch(() => props.selection, (newSelection) => {
  localSelection.value = newSelection;
});

const createSmartButton = (className) => {
  const button = document.createElement('a');
  button.href = '#';
  if (className.includes('elementor-control-tag-area')) {
    button.className = 'smart-press-button-small smart-fixed-btn-textarea';
  }else{
    button.className = 'smart-press-button-small smart-fixed-btn';
  }
  
  const img = document.createElement('img');
  img.src = props.buttonImagePath;
  button.appendChild(img);
  
  return button;
};

const createTinyMCEButton = () => {
  const button = document.createElement('a');
  button.href = '#';
  button.className = 'smart-press-button-small smartpress-tinymce-btn';
  
  const img = document.createElement('img');
  img.src = props.buttonImagePath;
  button.appendChild(img);
  
  return button;
};

// Check if the element is a TinyMCE editor textarea
const isTinyMCETextarea = (element) => {
  // Check if it has the wp-editor-area class
  if (element.classList.contains('wp-editor-area')) {
    return true;
  }
  
  // Check if it's inside a TinyMCE container
  const parent = element.closest('.wp-editor-container');
  if (parent) {
    return true;
  }
  
  // Check if its ID is used by TinyMCE
  if (element.id && typeof window.tinymce !== 'undefined' && window.tinymce.editors) {
    return window.tinymce.editors.some(editor => editor.id === element.id);
  }
  
  return false;
};

const addSmartButtons = () => {
  document.querySelectorAll(inputSelector).forEach(element => {
    // Skip if already has a button or is smart-edit-content or is a TinyMCE textarea
    if (!element.classList.contains('has-smart-button') && 
        element.id !== 'smart-edit-content' &&
        !isTinyMCETextarea(element)) {
          //pass element class name to createSmartButton
          const button = createSmartButton(element.className);
      element.insertAdjacentElement('beforebegin', button);
      // element.parentElement.appendChild(button);
      
      // Mark that this element has a button (but don't add smart-press-enabled yet)
      element.classList.add('has-smart-button');
      
      // Store elements for cleanup
      managedElements.value.add(element);
      managedElements.value.add(button);
    }
  });
};

// Add the smart-press-enabled class to an element
const enableSmartPressForElement = (element) => {
  if (element && !isTinyMCETextarea(element)) {
    element.classList.add('smart-press-enabled');
    activeElement.value = element;
  }
};

// Remove the smart-press-enabled class from current active element
const disableSmartPressForElement = () => {
  if (activeElement.value) {
    activeElement.value.classList.remove('smart-press-enabled');
    activeElement.value = null;
  }
};

const detectAndAddTinyMCEButtons = () => {
  // Clear any existing interval
  if (tinyMCECheckInterval.value) {
    clearInterval(tinyMCECheckInterval.value);
  }
  
  const addButtonToTinyMCE = (editorID) => {
    // Check if we already added a button to this editor
    if (tinyMCEEditors.value.has(editorID)) {
      return;
    }
    
    // Find the editor tabs where Visual/Text buttons are located
    const editorContainer = document.querySelector(`#wp-${editorID}-editor-tools`);
    if (!editorContainer) return;
    
    const switchButtons = editorContainer.querySelector('.wp-editor-tabs');
    if (!switchButtons || switchButtons.querySelector('.smart-press-button')) return;
    
    // Create and add our button
    const button = createTinyMCEButton();
    button.id = `smart-press-${editorID}`;
    button.setAttribute('data-wp-editor-id', editorID);
    
    // Add it to the tabs container
    switchButtons.appendChild(button);
    
    // Add click handler
    button.addEventListener('click', handleTinyMCEButtonClick);
    
    // Track that we've added a button to this editor
    tinyMCEEditors.value.add(editorID);
    managedElements.value.add(button);
    
    console.log(`Added SmartPress button to TinyMCE editor: ${editorID}`);
  };
  
  // First check: try immediate detection if TinyMCE is already loaded
  if (typeof window.tinymce !== 'undefined' && window.tinymce.editors) {
    window.tinymce.editors.forEach(editor => {
      addButtonToTinyMCE(editor.id);
    });
  }
  
  // Set up interval to check for TinyMCE (in case it loads after our component)
  tinyMCECheckInterval.value = setInterval(() => {
    if (typeof window.tinymce !== 'undefined' && window.tinymce.editors) {
      window.tinymce.editors.forEach(editor => {
        addButtonToTinyMCE(editor.id);
      });
    }
  }, 1000);
  
  // Stop checking after 30 seconds to prevent infinite interval
  setTimeout(() => {
    if (tinyMCECheckInterval.value) {
      clearInterval(tinyMCECheckInterval.value);
      tinyMCECheckInterval.value = null;
    }
  }, 30000);
};

const handleTinyMCEButtonClick = (event) => {
  event.preventDefault();
  
  const button = event.target.closest('.smart-press-button-small');
  if (!button) return;
  
  const editorID = button.getAttribute('data-wp-editor-id');
  if (!editorID) return;
  
  // Get the editor instance
  const editor = window.tinymce.get(editorID);
  if (!editor) return;
  
  // Get the editor container bounds
  const editorContainer = editor.getContainer();
  const rect = editorContainer.getBoundingClientRect();
  
  // Determine the current mode
  const isVisualMode = !document.getElementById(`${editorID}-html`).classList.contains('active');
  
  // Get the current content or selection
  let selectedText = '';
  let editorElement = editor; // Default to visual editor
  
  if (isVisualMode) {
    // Visual mode - try to get selected content from TinyMCE
    selectedText = editor.selection.getContent({format: 'text'}) || '';
    // If no selection, get current paragraph or empty string
    if (!selectedText) {
      try {
        const node = editor.selection.getNode();
        if (node) {
          selectedText = node.textContent || '';
        }
      } catch (e) {
        console.error('Error getting node content:', e);
      }
    }
  } else {
    // Text mode - try to get selected content from textarea
    const textarea = document.getElementById(editorID);
    if (textarea) {
      selectedText = textarea.value.substring(textarea.selectionStart, textarea.selectionEnd);
      editorElement = textarea; // Use textarea for text mode
    }
  }
  
  console.log('TinyMCE button clicked:', {
    editorID,
    selectedText,
    isVisualMode,
    editorElement: editorElement instanceof HTMLElement ? 'HTMLElement' : 'TinyMCE',
  });
  
  // Show popup
  emit('show-popup', selectedText, rect, rect, editorElement);
};

const getSelectionRect = (element) => ({
  top: element.offsetTop + element.scrollTop,
  right: element.offsetLeft + element.offsetWidth,
  bottom: element.offsetTop + element.scrollTop + element.offsetHeight,
  left: element.offsetLeft
});

const handleTextSelection = (event) => {
  const input = event.target;
  const selectedText = input.value.substring(input.selectionStart, input.selectionEnd);

  if (selectedText.trim().length > 0) {
    if (localSelection.value != selectedText) {
      emit('update-selection', selectedText);
      localSelection.value = selectedText;
   
      const inputRect = input.getBoundingClientRect();
      const selectionRect = getSelectionRect(input);
      emit('show-popup', selectedText, selectionRect, inputRect, input);
      
      // Add smart-press-enabled class to the input when popup shows
      enableSmartPressForElement(input);
    }
  } else {
    // Remove smart-press-enabled when popup closes
    disableSmartPressForElement();
    emit('close-popup');
  }
};

const handleSmartButtonClick = (event) => {
  console.log('handleSmartButtonClick', event);
  event.preventDefault();
  const button = event.target.closest('.smart-press-button-small');
  const input = button?.nextElementSibling;
  
  if (input?.matches(inputSelector)) {
    const buttonRect = button.getBoundingClientRect();
    const inputRect = input.getBoundingClientRect();
    
    emit('show-popup', input.value, buttonRect, inputRect, input);
    
    // Add smart-press-enabled class when button is clicked
    enableSmartPressForElement(input);
  }
};

// Store reference to our click listener for cleanup
const clickListener = ref(null);

// Create a custom method to handle popup closing
const handlePopupClose = () => {
  if (activeElement.value) {
    disableSmartPressForElement();
  }
};

const attachInputListeners = () => {
  if(props.customFields.on_selection){
  document.querySelectorAll(inputSelector).forEach(element => {
    element.addEventListener('select', handleTextSelection);
    element.addEventListener('keyup', handleTextSelection);
    element.addEventListener('mouseup', handleTextSelection);
  });
}
  
  document.querySelectorAll('.smart-press-button-small').forEach(button => {
    button.addEventListener('click', handleSmartButtonClick);
  });
};

// Add new function to initialize MutationObserver
const initMutationObserver = () => {
  observer.value = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        // Check if any of the added nodes contain our target inputs
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check for TinyMCE editor tabs or containers first
            if (node.querySelector?.('.wp-editor-tabs') || 
                node.classList?.contains('wp-editor-tools') ||
                node.classList?.contains('wp-editor-container')) {
              detectAndAddTinyMCEButtons();
              return;
            }
            
            // Check if the node itself is an input/textarea (but not TinyMCE)
            if (node.matches?.(inputSelector) && !isTinyMCETextarea(node)) {
              addSmartButtons();
              attachInputListeners();
            }
            
            // Check children of added node for inputs/textareas (but not TinyMCE)
            const inputs = node.querySelectorAll?.(inputSelector);
            if (inputs?.length) {
              // Only proceed if there are non-TinyMCE inputs
              let hasRegularInputs = false;
              inputs.forEach(input => {
                if (!isTinyMCETextarea(input)) {
                  hasRegularInputs = true;
                }
              });
              
              if (hasRegularInputs) {
                addSmartButtons();
                attachInputListeners();
              }
            }
          }
        });
      }
    });
  });

  // Start observing the entire document body
  observer.value.observe(document.body, {
    childList: true,
    subtree: true
  });
};

// Update onMounted to include observer initialization and event listening
onMounted(() => {
  addSmartButtons();
  attachInputListeners();
  detectAndAddTinyMCEButtons();
  initMutationObserver();
  
  // Listen for parent's closePopup events via custom event
  window.addEventListener('smartpress:close-popup', handlePopupClose);
  
  // Also listen for clicks on the close button
  clickListener.value = (event) => {
    // If user clicks the close button on the popup
    if (event.target.closest('.smart-press-popup a[class*="absolute"]')) {
      handlePopupClose();
    }
  };
  document.addEventListener('click', clickListener.value);
});

// Listen for parent component's close-popup event
// const onClosePopup = () => {
//   handlePopupClose();
// };



// Listen for selection changes
watch(() => props.selection, (newVal, oldVal) => {
  // When selection is cleared, it usually means popup closed
  if (oldVal && !newVal) {
    handlePopupClose();
  }
});

// Update cleanup to disconnect observer
const cleanup = () => {
  managedElements.value.forEach(element => {
    if (element.matches(inputSelector)) {
      element.removeEventListener('select', handleTextSelection);
      element.removeEventListener('keyup', handleTextSelection);
      element.removeEventListener('mouseup', handleTextSelection);
    } else if (element.matches('.smart-press-button-small.smartpress-tinymce-btn')) {
      // TinyMCE specific buttons
      element.removeEventListener('click', handleTinyMCEButtonClick);
    } else if (element.matches('.smart-press-button-small')) {
      // Regular input buttons
      element.removeEventListener('click', handleSmartButtonClick);
    }
  });
  managedElements.value.clear();
  tinyMCEEditors.value.clear();

  // Clear TinyMCE check interval
  if (tinyMCECheckInterval.value) {
    clearInterval(tinyMCECheckInterval.value);
    tinyMCECheckInterval.value = null;
  }

  // Disconnect the observer
  if (observer.value) {
    observer.value.disconnect();
    observer.value = null;
  }
  
  // Remove click event listener
  if (clickListener.value) {
    document.removeEventListener('click', clickListener.value);
    clickListener.value = null;
  }
  
  // Remove custom event listener
  window.removeEventListener('smartpress:close-popup', handlePopupClose);
};

onBeforeUnmount(() => {
  cleanup();
});
</script>

<style scoped>
.text-input-handler {
  display: contents;
}

</style> 